import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Filter, CheckCheck, Trash2, LayoutGrid, Table2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { fetchNotifications, markAsRead, markAllAsRead, deleteNotification } from '../store/notificationsSlice';
import { filterNotificationsForClient } from '../utils/notificationSecurity';
import { format } from 'date-fns';

export default function Notifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, unreadCount, total, loading } = useSelector((state) => state.notifications);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // CRITICAL SECURITY: Filter notifications for client users
  const secureNotifications = useMemo(() => 
    filterNotificationsForClient(notifications, user),
    [notifications, user]
  );

  // Calculate 15-day unread count for display
  const unreadLast15Days = useMemo(() => {
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
    
    return secureNotifications.filter(n => 
      !n.is_read && new Date(n.created_at) >= fifteenDaysAgo
    ).length;
  }, [secureNotifications]);

  const totalUnread = useMemo(() => 
    secureNotifications.filter(n => !n.is_read).length,
    [secureNotifications]
  );

  useEffect(() => {
    // Fetch all notifications (no server-side pagination)
    dispatch(fetchNotifications({
      skip: 0,
      limit: 1000, // Large limit to get all notifications
      unreadOnly: false
    }));
  }, [dispatch]);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      dispatch(markAsRead(notification.id));
    }
    
    // Navigate based on action_url or entity type
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.ticket_id) {
      navigate(`/tickets/${notification.ticket_id}`);
    } else if (notification.entity_type && notification.entity_id) {
      navigate(`/${notification.entity_type}s/${notification.entity_id}`);
    }
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleDelete = (notificationId, e) => {
    e.stopPropagation();
    dispatch(deleteNotification(notificationId));
  };

  // Apply filters
  const filteredByRead = filter === 'unread' 
    ? secureNotifications.filter(n => !n.is_read)
    : secureNotifications;

  const filteredNotifications = categoryFilter === 'all'
    ? filteredByRead
    : filteredByRead.filter(n => n.category === categoryFilter);

  // Client-side pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);

  const categories = [...new Set(secureNotifications.map(n => n.category).filter(Boolean))];

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, categoryFilter]);

  const getNotificationColor = (type) => {
    const colors = {
      'ticket_created': 'bg-green-50 border-green-200',
      'comment_added': 'bg-blue-50 border-blue-200',
      'status_changed': 'bg-purple-50 border-purple-200',
      'ticket_assigned': 'bg-amber-50 border-amber-200',
      'ticket_updated': 'bg-indigo-50 border-indigo-200',
    };
    return colors[type] || 'bg-slate-50 border-slate-200';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ticket_created':
        return '🎫';
      case 'comment_added':
        return '💬';
      case 'status_changed':
        return '🔄';
      case 'ticket_assigned':
        return '👤';
      case 'ticket_updated':
        return '✏️';
      default:
        return '📢';
    }
  };

  return (
    <div className="container mx-auto py-3 sm:py-4 md:py-6 px-3 sm:px-4 md:px-6 max-w-7xl">
      <Card className="shadow-sm">
        <CardHeader className="pb-3 sm:pb-4 md:pb-6">
          <div className="flex items-start sm:items-center justify-between flex-wrap gap-2 sm:gap-3 md:gap-4">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <CardTitle className="text-base sm:text-lg md:text-2xl font-bold">All Notifications</CardTitle>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {filteredNotifications.length} total • {totalUnread} unread
                </p>
              </div>
            </div>
            
            <div className="flex gap-1 sm:gap-2 flex-wrap sm:flex-nowrap">
              {totalUnread > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="gap-2 text-xs sm:text-sm"
                >
                  <CheckCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Mark all</span>
                  <span className="sm:hidden">Mark</span>
                </Button>
              )}
              
              <div className="flex border rounded-lg">
                <Button
                  variant={viewMode === 'card' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('card')}
                  className="rounded-r-none h-8 w-8 p-0"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="rounded-l-none h-8 w-8 p-0"
                >
                  <Table2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 md:space-y-6">
          {/* Filters */}
          <div className="flex gap-2 sm:gap-3 md:gap-4 flex-col sm:flex-row">
            <Tabs value={filter} onValueChange={setFilter} className="flex-1 min-w-[160px]">
              <TabsList className="grid w-full grid-cols-2 h-8 sm:h-9">
                <TabsTrigger value="all" className="text-xs sm:text-sm">
                  All ({secureNotifications.length})
                </TabsTrigger>
                <TabsTrigger value="unread" className="text-xs sm:text-sm">
                  Unread ({totalUnread})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {categories.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-48 h-8 sm:h-9 text-xs sm:text-sm">
                  <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs sm:text-sm">All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category} className="text-xs sm:text-sm">
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Notifications Content */}
          {loading && secureNotifications.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No notifications</p>
              <p className="text-sm">
                {filter === 'unread' ? "You're all caught up!" : "You don't have any notifications yet."}
              </p>
            </div>
          ) : viewMode === 'card' ? (
            <div className="space-y-3">
              {paginatedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`relative group p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    !notification.is_read ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-slate-200'
                  } ${getNotificationColor(notification.type)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl mt-1">{getTypeIcon(notification.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 mb-1">
                            {notification.title}
                            {!notification.is_read && (
                              <Badge variant="default" className="ml-2 text-xs">New</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-slate-600 mb-2">{notification.message}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>{format(new Date(notification.created_at), 'MMM dd, yyyy • HH:mm')}</span>
                            {notification.category && (
                              <Badge variant="outline" className="text-xs">
                                {notification.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                          onClick={(e) => handleDelete(notification.id, e)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 w-12"></th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Message</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedNotifications.map((notification) => (
                    <tr
                      key={notification.id}
                      className={`group cursor-pointer hover:bg-slate-50 transition-colors ${
                        !notification.is_read ? 'bg-blue-50/30' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <td className="py-3 px-4 text-center text-xl">
                        {getTypeIcon(notification.type)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{notification.title}</span>
                          {!notification.is_read && (
                            <Badge variant="default" className="text-xs">New</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 max-w-md">
                        <p className="text-sm text-slate-600 truncate">{notification.message}</p>
                      </td>
                      <td className="py-3 px-4">
                        {notification.category && (
                          <Badge variant="outline" className="text-xs">
                            {notification.category}
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {format(new Date(notification.created_at), 'MMM dd, yyyy HH:mm')}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                          onClick={(e) => handleDelete(notification.id, e)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredNotifications.length > itemsPerPage && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <p className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredNotifications.length)} of {filteredNotifications.length} notifications
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-10 h-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
